import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent } from 'react';
import { createFrameCoalescer } from 'Shared/editor/frame_coalescer';
import { nudgeScroll } from 'Shared/editor/use_drag_reorder';
import { dropLevelRange, dropSubtree, type EditorLine, subtreeBounds } from './line_rules';

type LineDragState = {
   /** Key of the dragged subtree's root line. */
   rootKey: string;
   /** Keys inside the dragged subtree, for styling and hit-test skips. */
   draggedKeys: Set<string>;
   /**
    * Keys of the lines OUTSIDE the subtree, in order — the `rest` list the
    * drop gap indexes into. Frozen at drag start (keys are stable), so gap
    * positions stay meaningful while the preview reorders rows on screen.
    */
   restKeys: string[];
   /** Pointer that started the drag; others are ignored while it runs. */
   pointerId: number;
   /** Pointer X at arm time; horizontal travel maps to level changes. */
   startX: number;
   startLevel: number;
   /** One indent stop in pixels, resolved from --line-indent at start. */
   indentPx: number;
   gap: number;
   level: number;
};

/**
 * A pointer that went down on a row but hasn't proven itself a drag yet.
 * Nothing is claimed: if it lifts without moving, the browser delivers the
 * tap to whatever was pressed (the bullet, the text).
 */
type ArmedDrag = {
   key: string;
   pointerId: number;
   startX: number;
   startY: number;
};

/** Movement that turns an armed pointer into a drag; taps stay clicks. */
const DRAG_START_DISTANCE = 6;

/**
 * Drag-and-drop for a step's line tree: vertical movement picks the drop
 * position, horizontal movement changes the indent level — one indent stop
 * per --line-indent of travel. A line drags with its whole subtree, and
 * both axes are clamped by dropLevelRange, so a drop can never produce a
 * tree the indent/move buttons couldn't have built.
 *
 * The whole row is the drag surface, but a press only ARMS a drag; it
 * becomes one after DRAG_START_DISTANCE of travel, so a plain tap on the
 * bullet or the text stays a click. Presses on interactive children
 * (buttons, links, the open Tiptap editor) never arm, except elements that
 * opt in with data-line-drag-through (the read-mode text, the bullet
 * trigger — their taps survive via the click path). Once a real drag
 * happens, the
 * release's click is swallowed so dropping over the text doesn't open its
 * editor.
 *
 * Like useDragReorder, a live drag follows the pointer through
 * window-level listeners, NOT pointer capture: every preview swap makes
 * React re-insert row DOM nodes, and Chrome silently releases capture on
 * an element that moves, leaving the drag deaf mid-flight.
 *
 * Touch: rows keep pan-y, so a vertical swipe on a line still scrolls the
 * page (the browser claims it and cancels the armed pointer); horizontal
 * travel starts the drag, full 2-axis once it does. The grip handle is
 * touch-action: none, so vertical touch drags work from there.
 *
 * While a drag is live, `previewLines` holds the list as the drop would
 * leave it — render rows from it so the tree is always shown in its
 * would-be shape.
 */
export function useLineDrag(lines: EditorLine[], onDrop: (next: EditorLine[]) => void) {
   // Distinguishes this editor's rows from any other line list mounted at
   // the same time (forceMount keeps hidden tabs in the DOM).
   const groupId = useId();
   const [dragState, setDragState] = useState<LineDragState | null>(null);
   // The refs keep the event handlers honest (pointer closures would
   // otherwise see stale snapshots); state drives the preview render.
   const dragRef = useRef<LineDragState | null>(null);
   const linesRef = useRef(lines);
   const onDropRef = useRef(onDrop);
   useEffect(() => {
      linesRef.current = lines;
      onDropRef.current = onDrop;
   });
   const update = useCallback((next: LineDragState | null) => {
      dragRef.current = next;
      setDragState(next);
   }, []);

   // Tears down the active pointer's window listeners; on drop, cancel, or
   // unmount mid-drag.
   const detachRef = useRef<(() => void) | null>(null);
   useEffect(() => () => detachRef.current?.(), []);

   // Cancels the live drag without committing. The pointer listeners stay
   // attached: the pointer is still down, and the eventual release must run
   // finish so its click is swallowed like any other drag's — detaching
   // here left that click live to open whatever the pointer released over.
   const cancelRef = useRef<(() => void) | null>(null);

   // Escape cancels without committing; keyboard events don't follow the
   // pointer, so listen globally while a drag runs.
   useEffect(() => {
      if (dragState === null) {
         return;
      }
      const cancelOnEscape = (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            cancelRef.current?.();
         }
      };
      window.addEventListener('keydown', cancelOnEscape);
      return () => window.removeEventListener('keydown', cancelOnEscape);
   }, [dragState === null]);

   /** Promote an armed pointer to a live drag. */
   const beginDrag = useCallback(
      (armed: ArmedDrag, anchor: HTMLElement) => {
         const currentLines = linesRef.current;
         const from = currentLines.findIndex(line => line.key === armed.key);
         if (from === -1) {
            return;
         }
         const { start, end } = subtreeBounds(currentLines, from);
         update({
            rootKey: armed.key,
            draggedKeys: new Set(currentLines.slice(start, end).map(line => line.key)),
            restKeys: [...currentLines.slice(0, start), ...currentLines.slice(end)].map(
               line => line.key
            ),
            pointerId: armed.pointerId,
            startX: armed.startX,
            startLevel: currentLines[start].level,
            indentPx: measureIndentPx(anchor),
            // rest keeps the lines before the subtree at the same indexes,
            // so `start` is the identity gap.
            gap: start,
            level: currentLines[start].level,
         });
      },
      [update]
   );

   /** Shared press handler: arms the pointer and follows it window-level. */
   const arm = useCallback(
      (key: string) => (event: PointerEvent<HTMLElement>) => {
         if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) {
            return;
         }
         // Presses on controls are clicks (toolbar buttons) or selections
         // (the open editor), not drags — except elements that opt in with
         // data-line-drag-through (the read-mode text, the bullet trigger):
         // their taps still land through the click path.
         const interactive = (event.target as HTMLElement).closest<HTMLElement>(
            'button, a, input, select, textarea, [contenteditable]'
         );
         if (interactive && !('lineDragThrough' in interactive.dataset)) {
            return;
         }
         // No text selection or focus theft while the press is in flight;
         // the click (tap-to-edit, bullet popover) is unaffected.
         event.preventDefault();
         detachRef.current?.();
         // Scroll nudging and indent measuring hang off an element inside
         // the list; it serves even after React moves it in preview swaps.
         const anchor = event.currentTarget;
         const armed: ArmedDrag = {
            key,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
         };
         // A cancelled press stays dead until release: without this flag the
         // move handler would read the cleared drag state as "not promoted
         // yet" and re-promote on the next pointer travel.
         let cancelled = false;

         // dragTo hit-tests (elementFromPoint) and measures (getBoundingClientRect)
         // on every call; a mouse can deliver far more pointermove events than
         // the display refreshes, so those reads are coalesced to at most one
         // per animation frame. Only the latest event in a frame is kept — the
         // in-between ones carried no information the last one doesn't
         // already include. The promotion check (arm -> live drag) stays
         // synchronous: it's pure arithmetic (Math.hypot), not a layout read.
         const moveCoalescer = createFrameCoalescer((moveEvent: globalThis.PointerEvent) => {
            const drag = dragRef.current;
            if (drag) {
               dragTo(drag, moveEvent, anchor, groupId, linesRef.current, update);
            }
         });

         const move = (moveEvent: globalThis.PointerEvent) => {
            if (moveEvent.pointerId !== armed.pointerId || cancelled) {
               return;
            }
            let drag = dragRef.current;
            if (drag === null) {
               if (
                  Math.hypot(moveEvent.clientX - armed.startX, moveEvent.clientY - armed.startY) <
                  DRAG_START_DISTANCE
               ) {
                  return;
               }
               beginDrag(armed, anchor);
               // Fall through: a fast flick may deliver its only move in
               // this very event, so it must also count as drag travel.
               drag = dragRef.current;
            }
            if (drag) {
               moveCoalescer.send(moveEvent);
            }
         };
         const finish = (endEvent: globalThis.PointerEvent, commit: boolean) => {
            if (endEvent.pointerId !== armed.pointerId) {
               return;
            }
            // A coalesced move can still be waiting for its frame when the
            // release lands (the two often arrive within the same tick,
            // ahead of the next paint) — flush it now so the drop commits
            // the position the pointer actually released over, not a
            // frame-stale one.
            moveCoalescer.flush();
            detach();
            const finished = dragRef.current;
            if (!finished && !cancelled) {
               // Never promoted: a plain tap, already on its way to
               // whatever was pressed.
               return;
            }
            update(null);
            // A drag's release still clicks when press and release share a
            // target; swallow it so a small drag over the text doesn't
            // also open the editor. A cancelled drag swallows too — the
            // user aborted the drag, not asked for a click.
            suppressNextClick();
            // A cancelled drag reaches here without a promoted drag, so
            // `finished` is only guaranteed once the cancelled path is out;
            // testing it here is what narrows it for the reorder below.
            if (!commit || cancelled || !finished) {
               return;
            }
            const currentLines = linesRef.current;
            const from = currentLines.findIndex(line => line.key === finished.rootKey);
            if (from === -1) {
               return;
            }
            const identity = finished.gap === from && finished.level === currentLines[from].level;
            if (!identity) {
               onDropRef.current(dropSubtree(currentLines, from, finished.gap, finished.level));
            }
         };
         const drop = (endEvent: globalThis.PointerEvent) => finish(endEvent, true);
         // Browser took the pointer back (a scroll pan, an OS gesture):
         // snap back, don't commit.
         const cancel = (endEvent: globalThis.PointerEvent) => finish(endEvent, false);
         const detach = () => {
            moveCoalescer.cancel();
            detachRef.current = null;
            cancelRef.current = null;
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', drop);
            window.removeEventListener('pointercancel', cancel);
         };
         window.addEventListener('pointermove', move);
         window.addEventListener('pointerup', drop);
         window.addEventListener('pointercancel', cancel);
         detachRef.current = detach;
         cancelRef.current = () => {
            cancelled = true;
            moveCoalescer.cancel();
            update(null);
         };
      },
      [groupId, update, beginDrag]
   );

   const handleProps = useCallback(
      (key: string) => ({
         // The grip exists for vertical touch drags: none lets them start
         // here, where the row's pan-y would hand them to the scroller.
         style: { touchAction: 'none' } as CSSProperties,
         onPointerDown: arm(key),
      }),
      [arm]
   );

   const rowProps = useCallback(
      (key: string) => ({
         'data-line-key': key,
         'data-line-group': groupId,
         // Vertical touch pans stay scrolls; horizontal travel is ours.
         style: { touchAction: 'pan-y pinch-zoom' } as CSSProperties,
         onPointerDown: arm(key),
      }),
      [groupId, arm]
   );

   /** The tree as the current drop would leave it; null when idle. */
   const previewLines = useMemo(() => {
      if (dragState === null) {
         return null;
      }
      const from = lines.findIndex(line => line.key === dragState.rootKey);
      if (from === -1) {
         return null;
      }
      return dropSubtree(lines, from, dragState.gap, dragState.level);
   }, [lines, dragState]);

   return {
      handleProps,
      rowProps,
      previewLines,
      /** Keys of the subtree being dragged, for styling; empty when idle. */
      draggingKeys: dragState?.draggedKeys ?? EMPTY_KEYS,
   };
}

export type LineDragRowProps = ReturnType<ReturnType<typeof useLineDrag>['rowProps']>;
export type LineDragHandleProps = ReturnType<ReturnType<typeof useLineDrag>['handleProps']>;

const EMPTY_KEYS: ReadonlySet<string> = new Set();

/** One pointer-move step of a live drag: recompute the gap and level. */
function dragTo(
   drag: LineDragState,
   moveEvent: globalThis.PointerEvent,
   anchor: HTMLElement,
   groupId: string,
   lines: EditorLine[],
   update: (next: LineDragState) => void
): void {
   nudgeScroll(moveEvent.clientY, anchor);
   const from = lines.findIndex(line => line.key === drag.rootKey);
   if (from === -1) {
      return;
   }

   // Vertical: the gap follows the non-dragged row under the pointer —
   // above its midpoint drops before it, below after. Over a dragged row
   // (or nothing), the gap stays put.
   let gap = drag.gap;
   const hovered = rowUnderPoint(moveEvent.clientX, moveEvent.clientY, groupId);
   if (hovered && !drag.draggedKeys.has(hovered.key)) {
      const restIndex = drag.restKeys.indexOf(hovered.key);
      if (restIndex !== -1) {
         const rect = hovered.element.getBoundingClientRect();
         gap = moveEvent.clientY < rect.top + rect.height / 2 ? restIndex : restIndex + 1;
      }
   }

   // Horizontal: one level per indent stop of travel from the grab point,
   // clamped to what the gap legally allows.
   const range = dropLevelRange(lines, from, gap);
   if (!range) {
      return;
   }
   const wanted = drag.startLevel + Math.round((moveEvent.clientX - drag.startX) / drag.indentPx);
   const level = Math.min(range.max, Math.max(range.min, wanted));
   if (gap !== drag.gap || level !== drag.level) {
      update({ ...drag, gap, level });
   }
}

/**
 * Swallow the click that follows a completed drag. The listener
 * self-expires in case the release produced no click at all (press and
 * release on different elements).
 */
function suppressNextClick(): void {
   const swallow = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
   };
   window.addEventListener('click', swallow, { capture: true, once: true });
   window.setTimeout(() => {
      window.removeEventListener('click', swallow, { capture: true });
   }, 150);
}

/**
 * The row under the pointer, found by hit-test: the drag listens at the
 * window, so the event target never identifies the hovered row. Rows from
 * another line list (a hidden tab's editor) are ignored.
 */
function rowUnderPoint(
   clientX: number,
   clientY: number,
   groupId: string
): { key: string; element: HTMLElement } | null {
   const row = document.elementFromPoint(clientX, clientY)?.closest<HTMLElement>('[data-line-key]');
   if (!row || row.dataset.lineGroup !== groupId || row.dataset.lineKey === undefined) {
      return null;
   }
   return { key: row.dataset.lineKey, element: row };
}

/**
 * One indent stop in pixels, measured where the drag lives so the value
 * tracks the row's actual --line-indent (it differs by breakpoint).
 */
function measureIndentPx(element: HTMLElement): number {
   const probe = document.createElement('div');
   probe.style.width = 'var(--line-indent)';
   probe.style.position = 'absolute';
   probe.style.visibility = 'hidden';
   element.append(probe);
   const width = probe.getBoundingClientRect().width;
   probe.remove();
   return width > 0 ? width : 24;
}
